import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard, ShieldCheck, CheckCircle2, Lock, ArrowLeft,
  Sparkles, Check, Package, DollarSign
} from "lucide-react";
import { toast } from "sonner";

const PRODUCTS = {
  p1: { name: "Local SEO & Review Automation", price: 500.00, interval: "one-time", desc: "Full local Google Business Profile rank optimization, automated SMS review invites, and citation cleanup." },
  p2: { name: "National SEO Package", price: 1000.00, interval: "one-time", desc: "Comprehensive backlink authority building, keyword clustering, and content funnel deployment." },
  p3: { name: "Enterprise SEO & Growth Retainer", price: 1500.00, interval: "monthly", desc: "Dedicated growth engineer, bi-weekly performance reviews, and multi-channel campaign automation." },
  p4: { name: "Full-Time Pro Retainer", price: 1200.00, interval: "monthly", desc: "Dedicated omnichannel visual marketing, email sequences, and client portal management." },
};

export default function PublicCheckout() {
  const { id } = useParams();
  const product = PRODUCTS[id] || {
    name: "Custom LaunchDeck Marketing Package",
    price: 950.00,
    interval: "one-time",
    desc: "Custom marketing solutions configured for your business."
  };

  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    cardNumber: "4242 •••• •••• 4242",
    expiry: "12/28",
    cvc: "882"
  });

  const handlePay = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setPaid(true);
      toast.success("Payment completed successfully!");
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-center items-center p-4 sm:p-8 font-sans">
      <div className="w-full max-w-xl space-y-6">
        {/* Brand Header */}
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-black border border-amber-500/50 flex items-center justify-center font-black text-amber-400 text-xs">
              iHUB
            </div>
            <span className="font-extrabold text-sm tracking-wide text-white font-heading">
              LaunchDeck
            </span>
          </Link>
          <div className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-950/60 border border-emerald-800/80 px-2.5 py-1 rounded-full">
            <Lock className="w-3.5 h-3.5" />
            <span>256-Bit SSL Encrypted Checkout</span>
          </div>
        </div>

        {paid ? (
          <Card className="p-8 bg-slate-900 border-slate-800 text-slate-100 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-white">Payment Received!</h2>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              Thank you! Your payment of <strong className="text-white">${product.price.toFixed(2)}</strong> for <strong>{product.name}</strong> has been processed. A receipt and client portal invite have been sent to your email.
            </p>
            <div className="pt-4">
              <Link to="/portal/colibri-client">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                  Access Your Client Portal →
                </Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Product Summary Card */}
            <Card className="p-6 bg-slate-900 border-slate-800 text-slate-100 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] uppercase font-bold">
                    {product.interval === "monthly" ? "Recurring Monthly Subscription" : "One-Time Payment"}
                  </Badge>
                  <h1 className="text-xl font-bold text-white mt-1.5">{product.name}</h1>
                  <p className="text-xs text-slate-400 mt-1">{product.desc}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black text-white font-sans">
                    ${product.price.toFixed(2)}
                  </div>
                  {product.interval === "monthly" && (
                    <span className="text-[11px] text-slate-400">/ month</span>
                  )}
                </div>
              </div>
            </Card>

            {/* Payment Details Form */}
            <Card className="p-6 bg-slate-900 border-slate-800 text-slate-100 space-y-5">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-400" />
                <span>Payment Information</span>
              </h2>

              <form onSubmit={handlePay} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-400 uppercase">Your Full Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="David Miller"
                    className="mt-1 bg-slate-950 border-slate-800 text-white text-xs"
                    required
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold text-slate-400 uppercase">Email Address (For Invoices & Portal Access)</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="david@example.com"
                    className="mt-1 bg-slate-950 border-slate-800 text-white text-xs"
                    required
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold text-slate-400 uppercase">Card Details</Label>
                  <div className="relative mt-1">
                    <Input
                      value={formData.cardNumber}
                      onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white font-sans text-xs pr-10"
                      required
                    />
                    <CreditCard className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-400 uppercase">Expires</Label>
                    <Input
                      value={formData.expiry}
                      onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
                      placeholder="MM/YY"
                      className="mt-1 bg-slate-950 border-slate-800 text-white font-sans text-xs"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-400 uppercase">CVC</Label>
                    <Input
                      value={formData.cvc}
                      onChange={(e) => setFormData({ ...formData, cvc: e.target.value })}
                      placeholder="CVC"
                      className="mt-1 bg-slate-950 border-slate-800 text-white font-sans text-xs"
                      required
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 text-sm shadow-md"
                  >
                    {loading ? "Processing..." : `Pay $${product.price.toFixed(2)} USD`}
                  </Button>
                </div>
              </form>

              <div className="flex items-center justify-center gap-4 pt-2 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Stripe Verified</span>
                <span>•</span>
                <span>Immediate Receipt Issued</span>
                <span>•</span>
                <span>Cancel Anytime</span>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

